import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BlogEntryDTO } from '../DTO/BlogEntryDTO';
import { BlogRowSkeleton } from './Skeleton';
import { PageHeader } from './PageHeader';
import { TrailEnd, TrailStart, Waypoint } from './Section';
import { postPreview, readingTime } from '../lib/format';

const byNewest = (a: BlogEntryDTO, b: BlogEntryDTO) =>
    (b.publishDate ?? '').localeCompare(a.publishDate ?? '');

function PostEntry({ post }: { post: BlogEntryDTO }) {
    const href = `/blog/${post.slug}`;
    const date = new Date(post.publishDate);

    return (
        <Waypoint
            as="article"
            marker={
                <>
                    <time dateTime={post.publishDate} className="flex flex-col gap-1">
                        <span className="font-display text-2xl md:text-[1.75rem] font-semibold leading-tight text-ink">
                            {date.getFullYear()}
                        </span>
                        <span className="label">
                            {date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                        </span>
                    </time>
                    <span className="label">{readingTime(post.content)}</span>
                </>
            }
        >
            <div className="flex flex-col gap-3 max-w-[42rem]">
                <h2 className="text-2xl md:text-[1.75rem] m-0">
                    <Link to={href} className="hover:text-loden">{post.title}</Link>
                </h2>
                <p className="text-muted m-0">{postPreview(post.content, 260)}</p>
                <Link to={href} className="link font-label self-start mt-1">
                    Read the post<span className="sr-only">: {post.title}</span>
                </Link>
            </div>
        </Waypoint>
    );
}

export default function BlogList() {
    const [posts, setPosts]     = useState<BlogEntryDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(false);

    useEffect(() => {
        axios.get<BlogEntryDTO[]>('/api/blog')
            .then(res => setPosts([...res.data].sort(byNewest)))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, []);

    const header = (
        <PageHeader
            title="Blog"
            lead="Thoughts, project write-ups, and notes along the way."
        />
    );

    if (loading) return <BlogRowSkeleton />;

    if (error || posts.length === 0) {
        return (
            <>
                {header}
                <p className="page-container py-16 text-muted">
                    {error
                        ? 'The posts could not be loaded. Reload the page to try again.'
                        : 'No posts published yet.'}
                </p>
            </>
        );
    }

    return (
        <>
            {header}
            <TrailStart label={`${posts.length} ${posts.length === 1 ? 'post' : 'posts'}, newest first`} />
            {posts.map(post => <PostEntry key={post.id} post={post} />)}
            <TrailEnd />
            <div className="pb-12" />
        </>
    );
}
